/**
 * ToonOutro - 깨봉툰 아웃트로 퀴즈 (qn_o)
 *
 * 각 페이지: 이미지 + 텍스트 + 2지선다 퀴즈
 * 정답 → 초록 + Lottie 애니메이션 + 효과음, 오답 → 빨간 + 흔들림 + 효과음
 * 정답 시에만 다음 페이지 이동 가능. DB 저장 없음.
 */
import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Lottie from 'lottie-react'
import { ChevronLeft, Volume2 } from 'lucide-react'
import { useToonPlayer } from './useToonPlayer'
import buttonAnimation from '../../assets/toon/lottie/toon_button_reaction.json'
import rightAnimation from '../../assets/toon/lottie/toon_outro_right.json'
import wrongAnimation from '../../assets/toon/lottie/toon_outro_wrong.json'
import rightSoundSrc from '../../assets/toon/sounds/toon_outro_right_sound.mp3'
import wrongSoundSrc from '../../assets/toon/sounds/toon_outro_wrong_sound.mp3'
import type { ToonOutroProps, ToonQuizCard } from './types'

type AnswerKey = 'answer_0' | 'answer_1' | null

export function ToonOutro({ cards, onComplete, autoPlayAudio = false }: ToonOutroProps) {
  const {
    currentPage,
    direction,
    isFirst,
    isLast,
    goNext,
    goPrev,
    playAudio,
    playSoundEffect,
  } = useToonPlayer({ totalPages: cards.length, autoPlayAudio })

  // 퀴즈 상태 (페이지별)
  const [answeredPages, setAnsweredPages] = useState<Set<number>>(new Set())
  const [playingAnimation, setPlayingAnimation] = useState<AnswerKey>(null)
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null)
  const [showCompleteButton, setShowCompleteButton] = useState(false)

  const card = cards[currentPage]
  const isPageAnswered = answeredPages.has(currentPage)

  // 페이지 전환 시 상태 리셋 + 오디오 재생
  useEffect(() => {
    setPlayingAnimation(null)
    setIsCorrect(null)
    setShowCompleteButton(false)
    if (card?.audioUrl) {
      playAudio(card.audioUrl)
    }
  }, [currentPage, card?.audioUrl, playAudio])

  const checkAnswer = useCallback(
    (answer: AnswerKey) => {
      if (!card || isPageAnswered || !answer) return

      const correct = answer === card.correctAnswer
      setIsCorrect(correct)
      setPlayingAnimation(answer)

      if (correct) {
        setAnsweredPages((prev) => new Set(prev).add(currentPage))
        setTimeout(() => playSoundEffect(rightSoundSrc), 800)
      } else {
        playSoundEffect(wrongSoundSrc)
      }
    },
    [card, currentPage, isPageAnswered, playSoundEffect]
  )

  const handleAnimationComplete = useCallback(() => {
    if (isCorrect) {
      setShowCompleteButton(true)
    }
    setPlayingAnimation(null)
    setIsCorrect(null)
  }, [isCorrect])

  const handleNext = useCallback(() => {
    setShowCompleteButton(false)
    goNext()
  }, [goNext])

  const slideVariants = {
    enter: (dir: string) => ({
      x: dir === 'right' ? 300 : -300,
      opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (dir: string) => ({
      x: dir === 'right' ? -300 : 300,
      opacity: 0,
    }),
  }

  const getButtonClasses = (answerKey: AnswerKey, toon: ToonQuizCard) => {
    const base =
      'relative h-20 w-full max-w-[50%] rounded-2xl text-white font-semibold text-base active:translate-y-2 transition-all duration-300'

    const isThisAnswer = playingAnimation === answerKey
    const isCorrectAnswer = toon.correctAnswer === answerKey
    const pageAnswered = answeredPages.has(currentPage)

    if (pageAnswered && isCorrectAnswer) {
      return `${base} bg-emerald-400 [box-shadow:unset]`
    }
    if (isThisAnswer && isCorrect === true) {
      return `${base} bg-emerald-400 [box-shadow:unset]`
    }
    if (isThisAnswer && isCorrect === false) {
      return `${base} bg-red-500 [box-shadow:0px_8px_0px_#D4524A,0px_16px_0px_rgba(0,0,0,0.1)]`
    }
    return `${base} bg-sky-400 [box-shadow:0px_8px_0px_#2E96CE,0px_16px_0px_rgba(0,0,0,0.1)] hover:opacity-80`
  }

  const getButtonAnimation = (answerKey: AnswerKey, toon: ToonQuizCard) => {
    const isThisAnswer = playingAnimation === answerKey
    const otherKey: AnswerKey = answerKey === 'answer_0' ? 'answer_1' : 'answer_0'
    const isOtherCorrect = playingAnimation === otherKey && isCorrect === true
    const pageAnswered = answeredPages.has(currentPage)
    const isCorrectAnswer = toon.correctAnswer === answerKey

    // 오답 흔들림
    if (isThisAnswer && isCorrect === false) {
      return { y: [-40, 40, -20, 20, 0] }
    }
    // 정답 시 중앙으로 이동
    if ((isThisAnswer && isCorrect === true) || (pageAnswered && isCorrectAnswer)) {
      return { x: answerKey === 'answer_0' ? '50%' : '-50%' }
    }
    // 상대 정답 시 사라짐
    if (isOtherCorrect || (pageAnswered && !isCorrectAnswer)) {
      return { y: '200%' }
    }
    return { x: 0, y: 0 }
  }

  return (
    <div className="relative w-full max-w-[720px] md:max-w-[800px] mx-auto">
      {/* 메인 컨테이너 */}
        <div className="relative w-full h-[600px] overflow-hidden bg-white rounded-3xl shadow-xl">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentPage}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="flex flex-col items-center w-full h-full p-8"
            >
              {/* 텍스트 + 오디오 */}
              {card?.text && (
                <div className="relative w-full py-4 pl-16 pr-4 font-medium text-center text-gray-700 bg-gray-100 rounded-2xl text-base md:text-lg">
                  {card.audioUrl && (
                    <button
                      className="absolute left-4 top-3 w-10 h-10 flex items-center justify-center rounded-xl bg-orange-400 hover:bg-orange-500 transition-colors"
                      onClick={() => playAudio(card.audioUrl)}
                    >
                      <Volume2 className="w-5 h-5 text-white" />
                    </button>
                  )}
                  {card.text}
                </div>
              )}

              {/* 이미지 */}
              {card?.imageUrl && (
                <div
                  className="w-full md:w-[640px] h-[280px] bg-no-repeat bg-center mt-6 bg-contain"
                  style={{ backgroundImage: `url(${card.imageUrl})` }}
                />
              )}

              {/* 2지선다 퀴즈 버튼 */}
              {card?.answer0 && card?.answer1 && (
                <div className="absolute flex justify-center w-full gap-4 px-8 bottom-8">
                  {(['answer_0', 'answer_1'] as const).map((answerKey) => {
                    const text = answerKey === 'answer_0' ? card.answer0 : card.answer1
                    return (
                      <motion.button
                        key={answerKey}
                        animate={getButtonAnimation(answerKey, card)}
                        transition={{ duration: 0.5, ease: 'linear' }}
                        onAnimationComplete={
                          playingAnimation === answerKey ? handleAnimationComplete : undefined
                        }
                        className={getButtonClasses(answerKey, card)}
                        onClick={() => checkAnswer(answerKey)}
                        disabled={isPageAnswered}
                      >
                        {text}
                        {/* Lottie 피드백 */}
                        {playingAnimation === answerKey && (
                          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                            <Lottie
                              animationData={isCorrect ? rightAnimation : wrongAnimation}
                              loop={false}
                              autoplay
                              style={{ width: 120, height: 120 }}
                            />
                          </div>
                        )}
                      </motion.button>
                    )
                  })}
                </div>
              )}

              {/* 완료 버튼 (마지막 페이지 + 정답 시) */}
              {isLast && showCompleteButton && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.3, ease: 'easeOut' }}
                  className="absolute bottom-8 right-8 px-6 py-2 min-w-[120px] font-bold text-white bg-orange-400 rounded-2xl
                    [box-shadow:0px_4px_0px_#E88A1C,0px_8px_0px_rgba(0,0,0,0.2)]
                    hover:bg-orange-500 active:translate-y-1 active:[box-shadow:0px_2px_0px_#E88A1C] transition-all"
                  onClick={onComplete}
                >
                  완료
                </motion.button>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Prev 버튼 */}
        {!isFirst && (
          <button
            onClick={goPrev}
            className="absolute top-1/2 left-2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center
              [box-shadow:0px_2px_0px_rgba(0,0,0,0.1)] hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-gray-500" />
          </button>
        )}

        {/* Next 버튼 (정답 후에만, 마지막 페이지 제외) */}
        {isPageAnswered && !isLast && (
          <button
            onClick={handleNext}
            className="absolute top-[calc(50%-20px)] -right-2 z-10"
          >
            <Lottie
              animationData={buttonAnimation}
              loop={false}
              autoplay
              style={{ width: 80 }}
            />
          </button>
        )}

        {/* 페이지 인디케이터 */}
        <div className="flex justify-center gap-1.5 mt-4">
          {cards.map((_, i) => (
            <div
              key={i}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                i === currentPage
                  ? 'bg-orange-400'
                  : answeredPages.has(i)
                    ? 'bg-emerald-400'
                    : 'bg-lc-gray-300'
              }`}
            />
          ))}
        </div>
    </div>
  )
}
